import { useCallback } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { useLibraryStore } from '@/stores/library-store'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { ChevronRight, Folder, FolderOpen, Plus } from 'lucide-react'
import { getAcceptedFileTypes, type LibraryFolder } from '@/lib/schemas'
import { ARCHIVE_ACCEPT_TYPES } from '@/lib/expand-upload-files'
import { prepareUploadsWithFeedback } from '@/lib/upload-feedback'
import { FolderContextMenu } from '@/components/FolderContextMenu'

interface FolderTreeItemProps {
  folder: LibraryFolder
  level: number
  /** Called when the user clicks the hover-revealed '+' to add a subfolder. */
  onNewSubfolder?: (parentId: string) => void
}

export function FolderTreeItem({ folder, level, onNewSubfolder }: FolderTreeItemProps) {
  const {
    selectedFolderId,
    expandedFolderIds,
    selectFolder,
    toggleFolderExpanded,
    getChildFolders,
    getFilteredDocuments,
    categoryFilter,
    hasActiveFilters,
    uploadLibraryDocuments,
    searchQuery,
    expiringFilter,
  } = useLibraryStore()
  const isViewer = useAuthStore((s) => s.role === 'viewer')
  const isAdmin = useAuthStore((s) => s.isPlatformAdmin || s.role === 'admin')
  const currentUserId = useAuthStore((s) => s.user?.id)

  // Filter-awareness: when a category filter is active, this folder is "in
  // scope" only if its own category is included. Folders out of scope dim and
  // show 0 in the count badge.
  const filtersActive = hasActiveFilters()
  const inScope = !filtersActive || categoryFilter.has(folder.category)

  // Dropzone for file uploads - directly upload files when dropped on folder
  // (ZIPs are opened in the browser first; refused types are named as skipped.)
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const dropped = [...acceptedFiles, ...fileRejections.map(({ file }) => file)]
      if (dropped.length === 0) return
      void (async () => {
        const files = await prepareUploadsWithFeedback(dropped)
        if (files.length > 0) await uploadLibraryDocuments(folder.id, files)
      })()
    },
    [folder.id, uploadLibraryDocuments]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { ...getAcceptedFileTypes(), ...ARCHIVE_ACCEPT_TYPES },
    noClick: true, // Don't open file picker on click
    noKeyboard: true, // Don't trigger on keyboard events
  })

  const isSelected = selectedFolderId === folder.id
  const isExpanded = expandedFolderIds.has(folder.id)
  const children = getChildFolders(folder.id)
  const hasChildren = children.length > 0

  // Filtered count — reflects what the document panel would show for this folder
  // under the current filter.
  //  - Excluded by an active category filter → 0 (the folder is out of scope;
  //    it also dims via `inScope` above).
  //  - Search / expiring lens active → derive from the loaded window (these are
  //    client-side lenses the server count doesn't know about; their result set
  //    is small enough to fit the window).
  //  - Otherwise use the authoritative, filter-aware count from the server
  //    (`folder.documentCount`), which already reflects the fact-bank / tag /
  //    archived filters and is correct no matter how many docs are paged in.
  //    This is what stops a folder going blank when a server-side filter clears.
  const excludedByCategory =
    categoryFilter.size > 0 && !categoryFilter.has(folder.category)
  const usesClientLens = searchQuery.trim().length > 0 || expiringFilter
  const documentCount = excludedByCategory
    ? 0
    : usesClientLens
      ? getFilteredDocuments(folder.id).length
      : folder.documentCount ?? 0

  const handleClick = () => {
    selectFolder(folder.id)
    // Also expand if has children and not already expanded
    if (hasChildren && !isExpanded) {
      toggleFolderExpanded(folder.id)
    }
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFolderExpanded(folder.id)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      <FolderContextMenu
        folder={folder}
        disabled={isViewer}
        canDelete={isAdmin || folder.createdBy === currentUserId}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          aria-expanded={hasChildren ? isExpanded : undefined}
          className={cn(
            'w-full flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors cursor-pointer',
            'hover:bg-accent group',
            isSelected ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground',
            isDragActive && 'bg-primary/10 ring-2 ring-primary ring-inset',
            // Out-of-scope under an active filter — dim but keep clickable
            !inScope && 'opacity-40'
          )}
          style={{ paddingLeft: `${level * 12 + 8}px` }}
        >
          {/* Expand/Collapse Toggle */}
          <button
            type="button"
            onClick={handleToggle}
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
            className={cn(
              'h-4 w-4 flex items-center justify-center rounded hover:bg-muted',
              !hasChildren && 'invisible'
            )}
          >
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform',
                isExpanded && 'rotate-90'
              )}
            />
          </button>

          {/* Folder Icon */}
          {isExpanded ? (
            <FolderOpen className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          ) : (
            <Folder className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          )}

          {/* Folder Name */}
          <span className="flex-1 text-left truncate">{folder.name}</span>

          {/* Hover-revealed: add subfolder. Hidden for viewers. */}
          {!isViewer && onNewSubfolder && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onNewSubfolder(folder.id)
                // Auto-expand to reveal the new subfolder when it lands
                if (hasChildren && !isExpanded) toggleFolderExpanded(folder.id)
              }}
              aria-label={`New subfolder in "${folder.name}"`}
              title={`New subfolder in "${folder.name}"`}
              className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Plus className="h-3 w-3" />
            </button>
          )}

          {/* Document count — filter-aware. Hidden when the hover '+' is
              visible to avoid stacking. When a filter is active we show even
              "0" so the user can see which folders have no matches. */}
          {(documentCount > 0 || filtersActive) && (
            <span
              className={cn(
                'text-meta tabular-nums',
                !isViewer && onNewSubfolder && 'group-hover:hidden'
              )}
            >
              {documentCount}
            </span>
          )}
        </div>
      </FolderContextMenu>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="space-y-0.5">
          {children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              level={level + 1}
              // Forward only when defined — exactOptionalPropertyTypes
              {...(onNewSubfolder && { onNewSubfolder })}
            />
          ))}
        </div>
      )}
    </div>
  )
}
